<template>
  <v-navigation-drawer
    class="d-flex flex-column"
    v-model="ui.drawer"
    :rail="ui.rail"
    expand-on-hover
    app
    color="grey-lighten-10"
    width="240"
    rail-width="56"
  >
    <!-- LOGO & HEADER -->
    <v-list-item class="sidebar-header" nav>
      <template #prepend>
        <v-img
          v-if="ui.rail"
          :src="logo"
          contain
          width="32"
          height="32"
          @click="$router.push({ name: 'Overview' })"
        />
        <v-img
          v-else
          :src="logo"
          contain
          height="58"
          width="200"
          @click="$router.push({ name: 'Overview' })"
        />
      </template>
    </v-list-item>
    <v-divider />

    <!-- NAV LINKS -->
    <v-list class="flex-1 overflow-y-auto" nav density="comfortable" v-model:opened="openedGroups">
      <template v-for="item in navLinks" :key="item.title">
        <v-divider v-if="item.divider" class="my-2" />
        <v-list-group v-else-if="item.children" :value="item.title" no-action append-icon="">
          <template #activator="{ props }">
            <v-list-item v-bind="props" nav density="comfortable" router link exact>
              <template #prepend>
                <v-icon color="primary">{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title v-if="!ui.rail">{{ item.title }}</v-list-item-title>
            </v-list-item>
          </template>
          <v-list-item
            v-for="child in item.children"
            :key="child.title"
            :to="child.action || child.href ? undefined : child.to"
            :href="child.href"
            :target="child.external ? '_blank' : undefined"
            router
            link
            exact
            class="pl-8"
            @click="child.action && child.action()"
          >
            <template #prepend>
              <v-icon small color="secondary">{{ child.icon }}</v-icon>
            </template>
            <v-list-item-title v-if="!ui.rail">{{ child.title }}</v-list-item-title>
          </v-list-item>
        </v-list-group>
        <v-list-item v-else :to="item.to" router link exact>
          <template #prepend>
            <v-icon color="primary">{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title v-if="!ui.rail">{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import logo from "@/assets/images/WaywyserLogo.png";

const ui = useUIStore();
const auth = useAuthStore();
const route = useRoute();

// All possible nav items. The `module` key ties an item to a per-customer flag
// from /api/auth/me.modules. Items without `module` are always visible.
const allNavLinks = [
  { title: "Overview", to: "/", icon: "mdi-view-dashboard-outline" },
  { title: "View All Map", to: "/view-map", icon: "mdi-map" },
  { title: "Zones", to: "/zones", icon: "mdi-view-dashboard-outline" },

  {
    module: "workflow",
    title: "Workflow",
    icon: "mdi-playlist-edit",
    children: [
      { title: "Inspections", to: "/inspections", icon: "mdi-clipboard-check-outline" },
      { title: "Findings", to: "/findings", icon: "mdi-alert-circle-outline" },
      { title: "Tasks", to: "/tasks", icon: "mdi-format-list-checks" },
    ],
  },
  {
    module: "reports",
    title: "Reports",
    icon: "mdi-file-document",
    children: [
      { title: "Reports", to: "/reports", icon: "mdi-file-document" },
      { title: "AI Report Builder", to: "/reports/ai-reports", icon: "mdi-robot" },
      { title: "AI Image Review", to: "/reports/ai-images", icon: "mdi-image-search" },
      { title: "Monitoring Zone Evidence", to: "/reports/monitoring-evidence", icon: "mdi-flask-outline" },
    ],
  },
  {
    // 2026-06-01: Promoted from a child of Reports to its own top-level item with
    // its own per-customer flag (Customer.ModuleGrantEvidence → modules.grantEvidence).
    module: "grantEvidence",
    title: "Grant Evidence Pack",
    to: "/reports/evidence-pack",
    icon: "mdi-file-certificate",
  },
  {
    module: "dashboards",
    title: "AI Dashboards",
    icon: "mdi-chart-box",
    children: [
      { title: "Browse Dashboards", to: "/ai-dashboards", icon: "mdi-view-dashboard" },
      { title: "New Widget", to: "/ai-dashboards/widget/new", icon: "mdi-plus-circle" },
      { title: "Semantic Designer", to: "/ai-dashboards/datasets", icon: "mdi-database-cog" },
    ],
  },
  {
    module: "regulatoryReporting",
    title: "Reporting (MRV)",
    icon: "mdi-leaf-circle-outline",
    children: [
      { title: "Overview", to: "/reporting", icon: "mdi-view-dashboard-variant" },
      { title: "Projects / Obligations", to: "/reporting/projects", icon: "mdi-folder-multiple" },
      { title: "Evidence Review", to: "/reporting/evidence", icon: "mdi-clipboard-search" },
      { title: "Generate Output", to: "/reporting/generate", icon: "mdi-file-document-arrow-right" },
      { title: "Submissions Log", to: "/reporting/submissions", icon: "mdi-history" },
      { title: "Settings", to: "/reporting/settings", icon: "mdi-cog" },
    ],
  },
  {
    // 2026-05-23: Citizen Science MRV. Per-customer flag added 2026-06-01
    // (Customer.ModuleCitizenScience → auth/me.modules.citizenScience).
    module: "citizenScience",
    title: "Citizen Science",
    icon: "mdi-account-group-outline",
    children: [
      { title: "Verifier Queue", to: "/citizen-science/queue", icon: "mdi-inbox-arrow-down" },
      { title: "Survey Tokens", to: "/citizen-science/tokens", icon: "mdi-qrcode" },
    ],
  },
  {
    module: "pm",
    title: "PM and Assets",
    icon: "mdi-domain",
    children: [
      { title: "Dashboard", to: "/pm/dashboard", icon: "mdi-chart-bar" },
      { title: "Assets", to: "/assets", icon: "mdi-domain" },
      { title: "Assets Map", to: "/pm/assetmap", icon: "mdi-map" },
      { title: "Master Cycles", to: "/pm/master-cycles", icon: "mdi-calendar-multiple" },
      { title: "Cycles", to: "/cycles", icon: "mdi-calendar-clock" },
      { title: "Work Orders", to: "/workorders", icon: "mdi-clipboard-text" },
      { title: "Schedule", to: "/schedule", icon: "mdi-calendar-month" },
      { title: "Working Calendar", to: "/pm/working-calendar", icon: "mdi-calendar" },
      { title: "Employee Loading", to: "/employee-loading", icon: "mdi-account-clock" },
    ],
  },
  {
    module: "iot",
    title: "IoT",
    icon: "mdi-radar",
    children: [
      { title: "Dashboard", to: "/iot/dashboard", icon: "mdi-chart-line" },
      { title: "Devices", to: "/iot/devices", icon: "mdi-lan-connect" },
      { title: "Subscriptions", to: "/iot/subscriptions", icon: "mdi-cloud-sync-outline" },
      { title: "Alerts", to: "/iot/alerts", icon: "mdi-alert-circle" },
    ],
  },
  { module: "weather", title: "Weather", to: "/weather", icon: "mdi-weather-partly-cloudy" },
  {
    module: "team",
    title: "Team",
    icon: "mdi-account-group",
    children: [
      { title: "Team", to: "/teams", icon: "mdi-account-group-outline" },
      { title: "Users", to: "/users", icon: "mdi-account-multiple-outline" },
    ],
  },
  { divider: true },
  {
    module: "utilities",
    title: "Utilities",
    icon: "mdi-tools",
    children: [
      { title: "Form Design", to: "/forms", icon: "mdi-form-select" },
      { title: "Report Templates", to: "/utilities/report-templates", icon: "mdi-file-document-edit" },
      { title: "Email Templates", to: "/utilities/email-templates", icon: "mdi-email-edit" },
      { title: "Lookups", to: "/utilities/lookups", icon: "mdi-format-list-bulleted" },
      { title: "Map Overlays", to: "/utilities/overlays", icon: "mdi-map-marker-multiple" },
      { title: "Map Overlays Settings", to: "/utilities/map-overlays", icon: "mdi-layers-edit" },
      { title: "Customer Settings", to: "/utilities/map-settings", icon: "mdi-cog" },
      { title: "Audit Trail", to: "/utilities/audit-trail", icon: "mdi-clipboard-text-clock" },
      { title: "Comms Log", to: "/utilities/comms-log", icon: "mdi-email-outline" },
      { title: "About", action: () => ui.openAbout(), icon: "mdi-information-outline" },
    ],
  },
];

// System admin sees everything; otherwise hide groups whose flag is false.
// authMe.modules may not have loaded yet — treat unknown as enabled.
const navLinks = computed(() => {
  const mods = auth.authMe?.modules;
  const isAdmin = !!auth.authMe?.isSystemAdmin;
  if (isAdmin || !mods) return allNavLinks;
  return allNavLinks.filter((item) => {
    if (!item.module) return true;
    return mods[item.module] !== false;
  });
});

// Accordion behaviour: only one group open at a time.
const openedGroups = ref([]);

// 1) Auto-expand the section that owns the current route and collapse the rest.
//    Navigating to a top-level (non-group) item collapses any open group, so we
//    never show both a selected top-level item and a lingering open group.
watch(
  () => route.path,
  (path) => {
    const parent = navLinks.value.find(
      (i) => i.children && i.children.some((c) => c.to === path)
    );
    openedGroups.value = parent ? [parent.title] : [];
  },
  { immediate: true }
);

// 2) Manual clicks: when Vuetify opens a second group, keep only the newest one
//    so opening one collapses whatever was open before.
watch(openedGroups, (val, oldVal) => {
  if (val.length > 1) {
    const newest = val.find((t) => !oldVal.includes(t)) ?? val[val.length - 1];
    openedGroups.value = [newest];
  }
});
</script>

<style scoped>
.sidebar-header {
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  padding-top: 12px;
  padding-bottom: 6px;
}
.pl-8 {
  padding-left: 12px !important;
}
.profile-activator {
  cursor: pointer;
  padding: 12px 0;
}
.profile-menu {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.sidebar-profile {
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: #fafbfc;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.v-list-group__items .v-list-item {
  padding-inline-start: 15px !important;
}

/* Group headers (Workflow, IoT, …) are expand/collapse toggles — they never
   represent the current route. Vuetify still flags the header as "active" when
   one of its children is active, which painted a second grey next to the real
   selection. Force headers transparent (hover overlay still shows feedback). */
:deep(.v-list-group__header) {
  background-color: transparent !important;
}
:deep(.v-list-group__header:not(:hover) .v-list-item__overlay) {
  opacity: 0 !important;
}

/* One consistent highlight: only the actual active row — a top-level item or a
   group child — shows a brand-blue tint with a left accent bar (inset shadow,
   so there's no layout shift). */
:deep(.v-list-item--active:not(.v-list-group__header)) {
  background-color: rgba(25, 118, 210, 0.14);
  color: rgb(25, 118, 210);
  box-shadow: inset 3px 0 0 rgb(25, 118, 210);
}
:deep(.v-list-item--active:not(.v-list-group__header) .v-list-item__overlay) {
  opacity: 0;
}

.sidebar-profile {
  margin-left: 10px;
}

.v-field__input {
  opacity: 1;
  min-width: 150px !important;
}
</style>
